import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export type OverrideType = 'manual' | 'ai';
export type OverridesSortBy = 'decided_at' | 'sku' | 'attribute_name' | 'type';

export interface OverrideRow {
  authority_id: number;
  type: OverrideType;
  subscription_id: number;
  product_id: number;
  sku: string | null;
  assignment_id: number;
  attribute_path: string;
  attribute_name: string;
  attribute_documentation: string | null;
  value_type: string;
  min_occurs: number;
  max_occurs: number;
  allow_free_values: boolean;
  original_value: string | null;
  override_value: string | null;
  final_value: string | null;
  decided_by: string | null;
  decided_at: string | null;
  reason: string | null;
  suggestion_id: number | null;
  ai_source: string | null;
  ai_confidence: number | null;
  ai_reasoning: string | null;
}

export interface OverridesAttributeFacet {
  assignment_id: number;
  attribute_name: string;
  count: number;
}

export interface ListOverridesMeta {
  /** The full set the counts/facets/total aggregate over, echoed back by the API. */
  subscription_ids: number[];
  page: number;
  per_page: number;
  total: number;
  counts: { manual: number; ai: number };
  attributes: OverridesAttributeFacet[];
}

export interface ListOverridesResponse {
  data: OverrideRow[];
  meta: ListOverridesMeta;
}

export interface ListOverridesParams {
  /** One or more sales channels to aggregate over. Rows carry their own subscription_id. */
  subscriptionIds: number[];
  page?: number;
  perPage?: number;
  sortBy?: OverridesSortBy;
  sortDir?: 'asc' | 'desc';
  type?: OverrideType;
  /** Attribute facet filter. Empty/undefined means every attribute. */
  assignmentIds?: number[];
  sku?: string;
  decidedBy?: string;
  q?: string;
}

export const MAX_OVERRIDES_PER_PAGE = 200;
export const MAX_OVERRIDES_SUBSCRIPTION_IDS = 50;
export const MAX_OVERRIDES_ASSIGNMENT_IDS = 200;

export const listOverrides = async (
  scope: RequestScope,
  params: ListOverridesParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ListOverridesResponse>> => {
  try {
    const subscriptionIds = params.subscriptionIds;
    if (!Array.isArray(subscriptionIds) || subscriptionIds.length === 0) {
      throw new Error('At least one subscription ID is required');
    }
    if (subscriptionIds.length > MAX_OVERRIDES_SUBSCRIPTION_IDS) {
      throw new Error(`At most ${MAX_OVERRIDES_SUBSCRIPTION_IDS} subscription IDs may be requested at once`);
    }
    if (subscriptionIds.some((id) => !Number.isInteger(id) || id < 1)) {
      throw new Error('Valid subscription IDs are required');
    }
    if (params.perPage !== undefined && (params.perPage < 1 || params.perPage > MAX_OVERRIDES_PER_PAGE)) {
      throw new Error(`per_page must be between 1 and ${MAX_OVERRIDES_PER_PAGE}`);
    }
    const assignmentIds = params.assignmentIds ?? [];
    if (assignmentIds.length > MAX_OVERRIDES_ASSIGNMENT_IDS) {
      throw new Error(`At most ${MAX_OVERRIDES_ASSIGNMENT_IDS} attribute IDs may be requested at once`);
    }
    // `atts_attribute_assignments.id` is a plain auto-increment and the API validates
    // `assignment_ids.*` as `min:1`, so a 0 here is a client-side placeholder that would
    // come back as a 422 — reject it before it costs a round trip.
    if (assignmentIds.some((id) => !Number.isInteger(id) || id < 1)) {
      throw new Error('Valid attribute IDs are required');
    }

    // Values stay strings (not numbers) so a legitimate zero survives the truthiness
    // checks below; the array params (subscription_ids, assignment_ids) are exempt because
    // a non-empty array is always truthy, and axios's default serializer renders them as
    // `subscription_ids[]=1&subscription_ids[]=2` (no paramsSerializer is configured on
    // channelApi, so the default applies).
    const query: Record<string, string | number[]> = { subscription_ids: subscriptionIds };
    if (params.page !== undefined) query.page = String(params.page);
    if (params.perPage !== undefined) query.per_page = String(params.perPage);
    if (params.sortBy) query.sort_by = params.sortBy;
    if (params.sortDir) query.sort_dir = params.sortDir;
    if (params.type) query.type = params.type;
    // `assignment_id` (singular) is still accepted by the API for older callers; this one
    // only ever sends the multi-valued form.
    if (assignmentIds.length) query.assignment_ids = assignmentIds;
    if (params.sku) query.sku = params.sku;
    if (params.decidedBy) query.decided_by = params.decidedBy;
    if (params.q) query.q = params.q;

    // No subscription in the path and no `x-astur-subscription-id` header: the route is
    // org-scoped and reads the whole selected set off the query string. Sending the header
    // would pin the request to a single channel again.
    const result = await scopedChannelApi(scope).get<ListOverridesResponse>('atts/acendaai/overrides', {
      ...options,
      params: query,
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
